import { useCallback, useEffect, useMemo, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, Download, FileSpreadsheet, Archive, Loader2, FileText } from "lucide-react";
import { PageHead, Stat, Empty, RangePicker, selectCls, btnGhost, monthStartIso, todayIso, isoDate } from "@/components/erp/ErpUi";
import { InvoiceRow, NF_LABEL, NF_STATUS } from "@/components/fiscal/FiscalPanel";
import { SpedDialog } from "@/components/fiscal/SpedDialog";
import { useFeatures } from "@/lib/features";

const p2 = (n) => String(n).padStart(2, "0");
const monthRange = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  return [isoDate(first), isoDate(last)];
};
const curMonth = () => { const d = new Date(); return `${d.getFullYear()}-${p2(d.getMonth() + 1)}`; };

export default function OwnerInvoices() {
  const { user } = useAuth();
  const multi = (user?.shops || []).length > 1;
  const [range, setRange] = useState([monthStartIso(), todayIso()]);
  const [month, setMonth] = useState(curMonth());
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [scope, setScope] = useState(user?.shop_id === "all" ? "all" : "current");
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState("");
  const [spedOpen, setSpedOpen] = useState(false);
  const { enabled } = useFeatures();
  const params = useMemo(() => ({ from: range[0], to: range[1], status: status || undefined, kind: kind || undefined, scope }),
                        [range, status, kind, scope]);
  const load = useCallback(() => api.get("/fiscal/invoices", { params }).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))), [params]);
  useEffect(() => { load(); }, [load]);
  const applyMonth = (ym) => { setMonth(ym); if (ym) setRange(monthRange(ym)); };
  const download = async (what) => {
    setDownloading(what);
    try {
      const monthParams = { from: range[0], to: range[1], kind: kind || undefined, scope };
      if (what === "xlsx") monthParams.status = status || undefined;
      const path = what === "xlsx" ? "/fiscal/invoices/export.xlsx" : what === "zip" ? "/fiscal/invoices/xmls.zip" : "/fiscal/invoices/export.csv";
      const resp = await api.get(path, { params: monthParams, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      const tag = month || `${range[0]}-a-${range[1]}`;
      a.href = url;
      a.download = what === "xlsx" ? `notas-fiscais-${tag}.xlsx` : what === "zip" ? `xmls-notas-${tag}.zip` : `notas-fiscais-${tag}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (what === "zip") toast.success(`Pacote de XMLs baixado${resp.headers?.["x-count"] ? ` (${resp.headers["x-count"]} XMLs)` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setDownloading(""); }
  };
  const s = data?.summary;
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={Receipt} kicker="Fiscal" title="Notas fiscais">
        {enabled("fiscal.sped") && user?.shop_id !== "all" && (
          <Button data-testid="invoices-sped" disabled={!month} className="bg-green-brand hover:bg-green-brandDark text-white font-bold" onClick={() => setSpedOpen(true)}>
            <FileText className="h-4 w-4 mr-1" /> Gerar SPED do mês
          </Button>
        )}
        {enabled("fiscal.relatorio_contador") && <>
        <Button data-testid="invoices-export-xlsx" variant="outline" disabled={downloading === "xlsx"} className={btnGhost} onClick={() => download("xlsx")}>
          {downloading === "xlsx" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />} Planilha do mês (XLSX)
        </Button>
        <Button data-testid="invoices-export-xmls" variant="outline" disabled={downloading === "zip"} className={btnGhost} onClick={() => download("zip")}>
          {downloading === "zip" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Archive className="h-4 w-4 mr-1" />} XMLs autorizados (ZIP)
        </Button>
        </>}
        <Button data-testid="invoices-export-csv" variant="outline" disabled={downloading === "csv"} className={btnGhost} onClick={() => download("csv")}>
          {downloading === "csv" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} CSV
        </Button>
      </PageHead>
      <SpedDialog open={spedOpen} onOpenChange={setSpedOpen} month={month} />
      <p className="text-xs text-zinc-500 -mt-2 max-w-2xl">Relatório fiscal para o contador: a planilha traz tipo, número, chave, data, cliente, valor e status; o ZIP junta os XMLs autorizados do mês, prontos para o SPED Fiscal.</p>
      <div className="flex flex-wrap gap-2 items-center">
        <label className="flex items-center gap-2 text-xs text-zinc-400">
          Mês
          <input data-testid="invoices-month" type="month" value={month} onChange={(e) => applyMonth(e.target.value)} className="h-10 rounded-md border border-zinc-800 bg-zinc-900 px-2 text-sm text-zinc-100" />
        </label>
        <RangePicker from={range[0]} to={range[1]} onChange={(v) => { setRange(v); setMonth(""); }} testid="invoices-range" />
        <select data-testid="invoices-status" value={status} onChange={(e) => setStatus(e.target.value)} className={`${selectCls} w-auto`}>
          <option value="">Todos os status</option>{Object.entries(NF_STATUS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
        </select>
        <select data-testid="invoices-kind" value={kind} onChange={(e) => setKind(e.target.value)} className={`${selectCls} w-auto`}>
          <option value="">Todos os tipos</option>{Object.entries(NF_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {multi && <select data-testid="invoices-scope" value={scope} onChange={(e) => setScope(e.target.value)} className={`${selectCls} w-auto`}><option value="current">Esta unidade</option><option value="all">Todas as unidades</option></select>}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="invoices-count" label="Notas" value={s?.count ?? "…"} />
        <Stat testid="invoices-authorized" label="Autorizadas" value={s ? `${s.authorized} · ${brl(s.authorized_total)}` : "…"} tone="green" />
        <Stat testid="invoices-processing" label="Processando" value={s?.processing ?? "…"} tone="amber" />
        <Stat testid="invoices-rejected" label="Rejeitadas" value={s?.rejected ?? "…"} tone="red" />
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : data.items.length === 0 ? <Empty>Nenhuma nota no período.</Empty> : (
        <ul className="space-y-2">
          {data.items.map((inv) => (
            <div key={inv.id}>
              <div className="text-[11px] text-zinc-500 mb-1">{inv.shop_name && scope === "all" ? `${inv.shop_name} · ` : ""}<Link className="hover:text-green-neon" to={inv.source === "venda" ? `/owner/balcao/${inv.order_id}` : `/owner/ordens/${inv.order_id}`}>{inv.source === "venda" ? "Venda" : "OS"} #{inv.order_number || "—"}</Link></div>
              <InvoiceRow inv={inv} onChanged={load} />
            </div>
          ))}
        </ul>
      )}
    </div>
  );
}
